import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { deleteTeaching, listAdminTeachings } from "@/lib/admin.functions";
import { updateTeaching } from "@/lib/teachings.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pencil, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/ensinos")({
  component: AdminEnsinos,
});

function AdminEnsinos() {
  const fn = useServerFn(listAdminTeachings);
  const delFn = useServerFn(deleteTeaching);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-teachings", search],
    queryFn: () => fn({ data: { search, limit: 100, offset: 0 } }),
  });

  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-teachings"] });
      queryClient.invalidateQueries({ queryKey: ["recent-teachings"] });
      toast.success("Ensino removido");
    },
    onError: (e: any) => toast.error(e.message || "Erro"),
  });

  const shown = (t: any) => (t.custom_title?.trim() || t.title) as string;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">Ensinos</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            {data?.count ?? 0} ensinos no catálogo. Você pode editar o título exibido — o título
            original do YouTube permanece salvo no backend.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/adicionar">+ Adicionar vídeo manualmente</Link>
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="min-h-[70vh] animate-pulse rounded-xl bg-secondary" />
      ) : (data?.rows?.length ?? 0) === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
          Nenhum ensino encontrado. Vá em "Sincronizar YouTube" para importar.
        </div>
      ) : (
        <>
          {/* Desktop: tabela */}
          <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Título</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Parashá</th>
                  <th className="px-4 py-3">Turno</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data!.rows.map((t: any) => (
                  <tr
                    key={t.id}
                    className="border-b border-border last:border-0 hover:bg-secondary/30"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/ensinos/$id"
                        params={{ id: t.id }}
                        className="font-medium text-foreground hover:text-primary"
                      >
                        {shown(t).length > 80 ? shown(t).slice(0, 80) + "..." : shown(t)}
                      </Link>
                      {t.custom_title?.trim() && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          Original: {t.title.length > 70 ? t.title.slice(0, 70) + "…" : t.title}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t.teaching_date ? formatDateBR(t.teaching_date) : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t.parashiot ? `${t.parashiot.number}. ${t.parashiot.name_translit}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t.turno === "manha" ? "Manhã" : t.turno === "tarde" ? "Tarde" : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <EditTitleButton teaching={t} />
                        <DeleteButton
                          onConfirm={() => del.mutate(t.id)}
                          title={shown(t)}
                          disabled={del.isPending}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <ul className="grid gap-3 md:hidden">
            {data!.rows.map((t: any) => (
              <li key={t.id} className="rounded-xl border border-border bg-card p-4">
                <Link
                  to="/ensinos/$id"
                  params={{ id: t.id }}
                  className="block font-medium text-foreground hover:text-primary"
                >
                  {shown(t)}
                </Link>
                {t.custom_title?.trim() && (
                  <div className="mt-1 text-xs text-muted-foreground">Original: {t.title}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {t.teaching_date && <span>{formatDateBR(t.teaching_date)}</span>}
                  {t.parashiot && (
                    <span>
                      {t.parashiot.number}. {t.parashiot.name_translit}
                    </span>
                  )}
                  {t.turno && <span>{t.turno === "manha" ? "Manhã" : "Tarde"}</span>}
                </div>
                <div className="mt-3 flex justify-end gap-1">
                  <EditTitleButton teaching={t} />
                  <DeleteButton
                    onConfirm={() => del.mutate(t.id)}
                    title={shown(t)}
                    disabled={del.isPending}
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function EditTitleButton({ teaching }: { teaching: any }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>(teaching.custom_title ?? "");
  const updateFn = useServerFn(updateTeaching);
  const queryClient = useQueryClient();

  const m = useMutation({
    mutationFn: () =>
      updateFn({
        data: { id: teaching.id, custom_title: value.trim() ? value.trim() : null } as any,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-teachings"] });
      queryClient.invalidateQueries({ queryKey: ["recent-teachings"] });
      queryClient.invalidateQueries({ queryKey: ["teachings"] });
      queryClient.invalidateQueries({ queryKey: ["teaching", teaching.id] });
      toast.success("Título atualizado");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message || "Erro ao salvar"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-primary hover:bg-primary/10">
          <Pencil className="mr-1.5 h-4 w-4" /> Editar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar título do ensino</DialogTitle>
          <DialogDescription>
            Este título será exibido na plataforma. O título original do YouTube permanece salvo e é
            usado nas rotinas automáticas. Deixe em branco para voltar ao título padrão.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="original-title" className="text-xs text-muted-foreground">
              Título original (somente leitura)
            </Label>
            <Input
              id="original-title"
              value={teaching.title ?? ""}
              readOnly
              className="mt-1 bg-muted/40"
            />
          </div>
          <div>
            <Label htmlFor="custom-title">Título personalizado</Label>
            <Input
              id="custom-title"
              value={value}
              maxLength={300}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Ex.: 12/10/2024 | Bereshit"
              className="mt-1"
              autoFocus
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {value.trim().length}/300 caracteres
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={m.isPending}>
            Cancelar
          </Button>
          {teaching.custom_title?.trim() && (
            <Button
              variant="ghost"
              onClick={() => {
                setValue("");
                m.mutate();
              }}
              disabled={m.isPending}
            >
              Restaurar padrão
            </Button>
          )}
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            {m.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteButton({
  onConfirm,
  title,
  disabled,
}: {
  onConfirm: () => void;
  title: string;
  disabled?: boolean;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="mr-1.5 h-4 w-4" /> Remover
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remover este ensino?</AlertDialogTitle>
          <AlertDialogDescription>
            "{title.length > 100 ? title.slice(0, 100) + "..." : title}" será removido do catálogo.
            O vídeo continua no YouTube; ele poderá reaparecer numa próxima sincronização.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Remover
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
